import React from 'react';
import {useTranslation} from 'react-i18next';
import {TscButtonType, Glyphs, PopoverButton, TscPopoverOrigin, TscButtonGlyphPlacement, ControlButton, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import TabHeader from '../TabHeader';
import UploadAudioDescriptionModal from '../../modals/ReplaceMediaModal/UploadAudioDescriptionModal';
import EditAudioDescriptionMenu from './AudioDescriptionsMenu';
import AudioDescriptionApi from '../../../service/audioDescriptionApi';
import {StyledWarning, HideButtonText} from '../../util/StyledElements';
import {SetupWorkerState} from '../../../model/workerState';
import cssConstants from '../../../constants/cssConstants';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {themeStore} from '../../../context/themeProvider';
import {mediaStore, updateMediaState} from '../../../context/mediaProvider';
import {AlertModalPortal} from '../../util/ModalPortalHelper';

const Container = styled.div`
   margin-top: 1rem;
`;

const AddAudioButton = styled(ControlButton)`
   width: max-content;
   min-width: 13.5rem;
   
   @media screen and (max-width: ${cssConstants.accessibilityTabButtonBreakpoint}px) {
      min-width: 2rem;
   }   
`;

export const AudioDescriptionsBase: React.FC<IStateMappedProps> = ({theme, Hash, AudioDescriptionUrl, AudioDescriptionId}) => {
   const errorEl = React.useRef<HTMLDivElement>(null);
   const {t} = useTranslation();
   const [isModalDisplayed, setIsModalDisplayed] = React.useState(false);
   const [isAlertDisplayed, setAlertDisplayed] = React.useState(false);
   const [workerState, setWorkerState] = SetupWorkerState();

   const onToggleModal = () => {
      setIsModalDisplayed(!isModalDisplayed);
   };

   const downloadUrl = AudioDescriptionApi.getAudioDescriptionDownloadUrl(Hash);

   const onRemove = async () => {
      setAlertDisplayed(false);
      setWorkerState({isWorking: true, hasError: false});

      try {
         await AudioDescriptionApi.removeAudioDescription(Hash);
         updateMediaState({AudioDescriptionId: '', AudioDescriptionUrl: ''});
         window.location.reload();
      } catch (ex) {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   const onReplace = () => {
      setIsModalDisplayed(true);
   };

   const onToggleAlert = () => {
      setAlertDisplayed(!isAlertDisplayed);
   };

   const getFileName = (): string => {
      if (workerState.isWorking) {
         return t('accessibility.audioDescription.deleting');
      }

      if (AudioDescriptionUrl) {
         return AudioDescriptionUrl?.replace(/^.*[\\/]/, '').replace(/\?.*/, '');
      }

      return t('accessibility.audioDescription.none');
   };

   const addButton =
      (<HideButtonText breakpointWidth={cssConstants.accessibilityTabButtonBreakpoint} glyphPlacement={TscButtonGlyphPlacement.left}>
         <AddAudioButton
            label={t('accessibility.audioDescription.addAudioDescription')}
            buttonType={TscButtonType.secondary}
            themeName={theme}
            onClick={onToggleModal}
            glyph={AudioDescriptionId ? <Glyphs.Ellipsis16x16 /> : <Glyphs.Add16x16 />}
            glyphPlacement={TscButtonGlyphPlacement.left}
            disabled={workerState.isWorking}
            testId="audio-description-add-button"
         />
      </HideButtonText>)
   ;

   const editButton =
      (<PopoverButton
         buttonGlyph={<Glyphs.Ellipsis16x16 />}
         buttonType={TscButtonType.secondary}
         buttonTitle={t('accessibility.modify')}
         width="2rem"
         origin={TscPopoverOrigin.topRight}
         popoverMenu={<EditAudioDescriptionMenu url={downloadUrl} onRemove={onToggleAlert} onReplace={onReplace} />}
         themeName={theme}
         disabled={workerState.isWorking}
         testId="audio-description-edit-button"
         useMobileMenu={true}
         mobileContextMenuZindex={cssConstants.zIndex.mobileContextMenu}
         mobileContextMenuTitle={t('accessibility.modifyAudioDescription')}
      />)
   ;

   return (
      <Container>
         <TabHeader leftLabel={t('accessibility.audioDescription.label')} rightLabel={getFileName()} hideRightLabelInMobileView={false} button={AudioDescriptionId ? editButton : addButton} allowButtonOverflow={true} />
         {workerState.hasError && <StyledWarning id="error-message-delete" ref={errorEl}>{t('accessibility.audioDescription.deleteError')}</StyledWarning>}
         {isModalDisplayed && <UploadAudioDescriptionModal onClose={onToggleModal} isReplacing={!!AudioDescriptionId} />}
         {isAlertDisplayed && <AlertModalPortal title={t('accessibility.audioDescription.alert.title')} onClose={onToggleAlert} onConfirm={onRemove} confirmButtonType={TscButtonType.destructive} confirmContent={t('general.delete')} onDismiss={onToggleAlert} dismissButtonType={TscButtonType.secondary} dismissContent={t('general.cancel')}>{t('accessibility.audioDescription.alert.text')}</AlertModalPortal>}
      </Container>
   );
};

export interface IStateMappedProps {
   theme: TscThemeNames;
   Hash: string;
   AudioDescriptionUrl: string;
   AudioDescriptionId: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {theme}: Partial<IStateMappedProps>,
   {Hash, AudioDescriptionUrl, AudioDescriptionId}: Partial<IStateMappedProps>
): IStateMappedProps => ({theme, Hash, AudioDescriptionUrl, AudioDescriptionId});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(AudioDescriptionsBase);
